
import { Inject, Injectable, DOCUMENT } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { BehaviorSubject } from "rxjs";
import { UniversalStorage } from "../storages/universal.storage";

@Injectable()
export class LanguageService {
  public static readonly defaultLanguage: string = 'de-CH';
  private readonly storageKey: string = 'language.code';
  currentLanguage$ = new BehaviorSubject<string | null>(null);

  constructor(private readonly translateService: TranslateService,
    private readonly universalStorage: UniversalStorage,
    @Inject(DOCUMENT) private readonly document: Document) {
  }

  get currentLanguageCode(): string {
    return this.translateService.currentLang ?? LanguageService.defaultLanguage;
  }

  private getLanguageCodeFromUrl(queryParameterName: string): string | null {
    const urlParts = this.document.URL.split('?');

    if (urlParts.length > 1) {
      const queryParameterPart = urlParts[1];
      const queryParameterParts = queryParameterPart.split('&');
      const languagePart = queryParameterParts.find(part => part.startsWith(queryParameterName));

      const idPart = queryParameterParts.find(part => part.startsWith('id'));
      if (idPart) {
        this.universalStorage.setItem('foundation.lastSessionId', idPart?.split('=')[1] ?? null);
      }

      return languagePart?.split('=')[1] ?? null;
    }
    this.universalStorage.removeItem('foundation.lastSessionId');
    return null;
  }

  async init(): Promise<void> {
    // Try to get language from query parameters
    let language: string | null = null;

    if (!language) {
      language = this.mapLanguageCode(this.getLanguageCodeFromUrl('language'));
    }

    if (!language) {
      // If language was not provided (e.g. on reload) use the stored language if available
      const storedLanguage = this.getStoredLanguage();

      if (storedLanguage) {
        language = storedLanguage;
      }
      else {
        // Otherwise use the default language
        language = LanguageService.defaultLanguage;
      }
    }

    this.translateService.setDefaultLang(LanguageService.defaultLanguage);
    this.setLanguage(language);
  }

  mapLanguageCode(code: string | null): string | null {
    if (code?.startsWith('de')) {
      return LanguageService.defaultLanguage;
    }
    else if (code?.startsWith('en')) {
      return 'en-US';
    }
    else if (code?.startsWith('fr')) {
      return 'fr-CH';
    }
    else if (code?.startsWith('it')) {
      return 'it-CH';
    }

    return null;
  }

  setLanguage(code: string): void {
    this.storeLanguage(code);
    this.translateService.use(code).subscribe();
    this.currentLanguage$.next(code);
  }

  private storeLanguage(key: string): void {
    this.universalStorage.setItem(this.storageKey, key);
  }

  public getStoredLanguage(): string | null {
    return this.universalStorage.getItem(this.storageKey);
  }
}
